import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import styled from 'styled-components'

const LabelInput = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
`

const StyledInput = styled.input<{ $isFocused: boolean; $isInvalid: boolean }>`
  height: 32px;
  font-size: 16px;
  font-family: inherit;
  font-weight: 500;
  line-height: 1.4;
  color: white;
  border: 1px solid
    ${(props) =>
      props.$isInvalid
        ? 'rgba(248, 113, 113, 0.9)'
        : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 4px;
  outline: none;
  padding: 8px 12px;
  width: 100%;
  background: ${(props) =>
    props.$isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  transition: background-color 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    border-color: ${(props) =>
      props.$isInvalid
        ? 'rgba(248, 113, 113, 0.95)'
        : 'rgba(255, 255, 255, 0.6)'};
  }
`

const WarningText = styled.div`
  margin-top: 0.25rem;
  font-size: 0.65rem;
  color: rgba(248, 113, 113, 0.95);
`

export interface MarkupLabelInputRef {
  focus: () => void
}

export interface MarkupLabelInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  onEnter?: () => void
  onTabKey?: (e: React.KeyboardEvent) => void
  onEscape?: () => void
  saveOnBlur?: boolean
  requireNonEmpty?: boolean
  warningText?: string
}

export const MarkupLabelInput = forwardRef<
  MarkupLabelInputRef,
  MarkupLabelInputProps
>(
  (
    {
      value,
      onChange,
      placeholder = 'Enter label...',
      disabled = false,
      onEnter,
      onTabKey,
      onEscape,
      saveOnBlur = false,
      requireNonEmpty = false,
      warningText = 'Label is required.',
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const isInvalid = requireNonEmpty && (!value || value.trim().length === 0)

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
    }))

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (onEscape) {
          onEscape()
        }
        return
      }

      e.stopPropagation()

      if (e.key === 'Enter') {
        if (!isInvalid && onEnter) {
          onEnter()
        }
        inputRef.current?.blur()
      } else if (e.key === 'Tab') {
        e.preventDefault()
        onTabKey?.(e)
      }
    }

    const handleBlur = () => {
      setIsFocused(false)
      if (saveOnBlur && !isInvalid && onEnter) {
        onEnter()
      }
    }

    return (
      <LabelInput>
        <StyledInput
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
          onKeyUp={(e) => e.stopPropagation()}
          placeholder={placeholder}
          $isFocused={isFocused}
          $isInvalid={isInvalid}
          disabled={disabled}
        />
        {isInvalid && <WarningText>{warningText}</WarningText>}
      </LabelInput>
    )
  }
)

MarkupLabelInput.displayName = 'MarkupLabelInput'
